import { useEffect, useMemo } from 'react';
import {
  circle as turfCircle,
  point as turfPoint,
  booleanPointInPolygon,
} from '@turf/turf';
import { GlassPanel } from '@/components/glass/GlassPanel';
import { GlobalNav } from '@/components/dashboard/GlobalNav';
import { LeafletMap } from '@/components/map/LeafletMap';
import {
  MapFilterBar,
  MapFilterBarMobile,
} from '@/components/map/MapFilterBar';
import { MapLegend } from '@/components/map/MapLegend';
import { useMapData } from '@/hooks/useMapData';
import { useMapRankings } from '@/hooks/useMapRankings';
import { useFilters } from '@/hooks/useFilters';
import { MAP_DEFAULTS } from '@/lib/filterDefaults';
import type { AmenityCategory } from '@/lib/map-constants';
import type { RankedPropertyDTO } from '@/types/db';
import '@/components/map/leaflet-map.css';

/**
 * `/app/map` route component. Phase 08 wires together:
 *  - Plan 08-01: <LeafletMap> (plain Leaflet 1.9.4 + Turf.js classification)
 *  - Plan 08-02: <MapFilterBar> + <MapLegend> driven by useFilters URL state
 *
 * MAP-02 contract: this page reads the SAME `useFilters()` hook the dashboard
 * uses. Drag the radius on `/app/`, navigate here, see the same value. The
 * `cats` and `highlight` fields propagate the same way.
 *
 * The in-radius count rendered in <MapFilterBar> is computed here via Turf.js
 * — independently from <LeafletMap>'s internal classification — so the UI
 * count is decoupled from the map's imperative side. Both engines consume
 * identical inputs (filters.radius, filters.cats, mapData.{properties,
 * amenities}); they cannot drift.
 */
export function MapPage() {
  const query = useMapData();
  // MAP_DEFAULTS: on a bare /app/map URL, seed dog_park+beach within 0.5mi
  // with inRadiusOnly on (the page "loads filtered" to dog parks/beaches).
  const { filters, setFilters } = useFilters(MAP_DEFAULTS);

  // Plan 08-04 (gap closure for ROADMAP § Phase 08 SC #4): second TanStack
  // Query for the property-popup composite + per-category neighbor counts.
  // Keyed by `filters.radius` so the popup numerals match the radius the
  // user is currently viewing on the map. Failed/loading rankings → popup
  // gracefully degrades (placeholder '—') without blocking the map.
  const rankingsQuery = useMapRankings(filters.radius);

  // Map<property_id, row> for O(1) popup lookup. Recomputed only when the
  // rankings array reference changes (TanStack Query gives a stable
  // reference per cache slot — re-render on the same data does NOT
  // rebuild the Map).
  const rankings = useMemo<Map<number, RankedPropertyDTO>>(() => {
    const m = new Map<number, RankedPropertyDTO>();
    for (const r of rankingsQuery.data ?? []) {
      m.set(r.property_id, r);
    }
    return m;
  }, [rankingsQuery.data]);

  useEffect(() => {
    document.title = 'Map — House Hunter';
  }, []);

  const visibleCategories = useMemo(
    () => new Set<AmenityCategory>(filters.cats as AmenityCategory[]),
    [filters.cats],
  );

  // Per-category counts of the loaded amenity set (filter-aware
  // counts are computed below as `amenityCount`).
  const categoryCounts = useMemo<Record<AmenityCategory, number>>(() => {
    const counts: Record<AmenityCategory, number> = {
      dog_park: 0,
      beach: 0,
      daycare: 0,
      bar: 0,
    };
    for (const a of query.data?.amenities ?? []) {
      if (a.category in counts) {
        counts[a.category as AmenityCategory]++;
      }
    }
    return counts;
  }, [query.data]);

  // Sum of counts for the currently-visible categories.
  const amenityCount = useMemo(() => {
    let n = 0;
    for (const cat of visibleCategories) {
      n += categoryCounts[cat] ?? 0;
    }
    return n;
  }, [visibleCategories, categoryCounts]);

  const propertyCount = query.data?.properties.length ?? 0;

  // Mirrors LeafletMap.recalculateHighlights — pure, derived, no Leaflet
  // side-effects. Renders 32-step Turf circles around each visible amenity
  // and counts properties whose point falls in any one.
  const inRadiusCount = useMemo(() => {
    if (!query.data) return 0;
    const visibleAmenities = query.data.amenities.filter(
      (a) =>
        a.lat != null &&
        a.lon != null &&
        visibleCategories.has(a.category as AmenityCategory),
    );
    if (visibleAmenities.length === 0) return 0;
    const polygons = visibleAmenities.map((a) =>
      turfCircle([a.lon!, a.lat!], filters.radius, {
        units: 'miles',
        steps: 32,
      }),
    );
    let n = 0;
    for (const p of query.data.properties) {
      if (p.lat == null || p.lon == null) continue;
      const pt = turfPoint([p.lon, p.lat]);
      for (const poly of polygons) {
        if (booleanPointInPolygon(pt, poly)) {
          n++;
          break;
        }
      }
    }
    return n;
  }, [query.data, visibleCategories, filters.radius]);

  return (
    <>
      <GlobalNav />
      <main
        id="main"
        aria-label="Property and amenity map"
        className="h-[100dvh] flex flex-col bg-ocean-950 text-foam-50 pt-16"
      >
      {/* Filter bar — desktop + mobile rendered side-by-side; Tailwind
       *  responsive `hidden md:flex` / `md:hidden` picks one. */}
      {query.data && (
        <>
          <MapFilterBar
            filters={filters}
            setFilters={(patch) => setFilters(patch)}
            amenityCount={amenityCount}
            propertyCount={propertyCount}
            inRadiusCount={inRadiusCount}
          />
          <MapFilterBarMobile
            filters={filters}
            setFilters={(patch) => setFilters(patch)}
            amenityCount={amenityCount}
            propertyCount={propertyCount}
            inRadiusCount={inRadiusCount}
          />
        </>
      )}

      {/* Map area — fills the remaining column height below the filter bar.
       *  min-h-0 lets this flex child shrink so Leaflet (h-full) gets a real
       *  pixel height; the parent's 100dvh keeps the bottom zoom/attribution
       *  controls clear of mobile browser chrome. Overlays render absolutely. */}
      <div className="relative flex-1 min-h-0">
        {query.isLoading && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-[500]">
            <GlassPanel variant="strong" className="px-8 py-6 text-center">
              <p className="text-base font-semibold text-foam-50">
                Loading map…
              </p>
            </GlassPanel>
          </div>
        )}

        {query.isError && (
          <div className="absolute inset-0 flex items-center justify-center z-[500]">
            <GlassPanel
              variant="strong"
              className="px-8 py-6 text-center max-w-md"
            >
              <h2 className="text-xl font-semibold text-foam-50 mb-2">
                Map failed to load.
              </h2>
              <p className="text-sm text-foam-50/70 mb-4">
                Couldn&apos;t reach /api/map-data. Try reloading.
              </p>
              <button
                type="button"
                onClick={() => query.refetch()}
                className="inline-flex items-center justify-center h-10 px-4 rounded-md bg-bronze-500/20 border border-bronze-500/60 text-foam-50 font-semibold focus-visible:outline-2 focus-visible:outline-bronze-500/60 focus-visible:outline-offset-2 hover:bg-bronze-500/30"
              >
                Retry
              </button>
            </GlassPanel>
          </div>
        )}

        {query.data && (
          <>
            <LeafletMap
              mapData={query.data}
              radiusMiles={filters.radius}
              visibleCategories={visibleCategories}
              highlightInRadius={filters.highlight}
              showOnlyInRadius={filters.inRadiusOnly}
              showRadiusCircles={filters.showRadiusCircles}
              showAmenityPins={filters.showAmenityPins}
              rankings={rankings}
            />
            <MapLegend
              visibleCategories={visibleCategories}
              categoryCounts={categoryCounts}
              radiusMiles={filters.radius}
            />
          </>
        )}
      </div>
      </main>
    </>
  );
}
